import {
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { serverUrl } from '../../config';
import { routes } from '../../data/routes';
import { Session, sessionAtom, useSession } from '../../lib/Session';
import { FormContainer } from '../FormContainer';
import { LinkText } from '../LinkText';
import { TextField } from '../TextField';

const loginFormSchema = yup.object().shape({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('An email is required')
    .max(120),
  password: yup.string().required('Please enter a password').max(140),
});

const initialFormValues = {
  email: '',
  password: '',
};

type LogInValues = typeof initialFormValues;

const postLogIn = (values: LogInValues) => {
  const encodedBasicAuth = btoa(`${values.email}:${values.password}`);
  return fetch(`${serverUrl}/api/actions/log-in`, {
    method: 'post',
    headers: {
      authorization: `Basic ${encodedBasicAuth}`,
    },
  }).then((res) => res.json());
};

const submitLogIn = async (
  values: LogInValues,
  setError: React.Dispatch<React.SetStateAction<string | undefined>>,
  onLogIn: (s: Session) => void
) => {
  try {
    const res = await postLogIn(values);
    if (res.error === 'Unauthorized') {
      setError('Username and password combination does not match');
    } else {
      onLogIn(res.session);
    }
  } catch (error) {
    setError('Something went wrong. Please try again in a minute.');
  }
};

const useLogIn = () => {
  const [submissionError, setSubmissionError] = useState<string | undefined>();
  const { logIn } = useSession();
  const navigate = useNavigate();

  const onSuccess = useCallback(
    (s: Session) => {
      logIn(s);
      navigate(routes.root);
    },
    [logIn, navigate]
  );

  const onSubmit = useCallback(
    async (values: LogInValues) => await submitLogIn(values, setSubmissionError, onSuccess),
    [setSubmissionError, onSuccess]
  );

  return { submissionError, onSubmit };
};

const LogInForm = () => {
  const { submissionError, onSubmit } = useLogIn();

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={loginFormSchema}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting, isValid }) => (
        <Form>
          <Stack spacing={4}>
            <TextField name="email" label="Email" touched={touched.email} error={errors.email} />
            <TextField
              name="password"
              label="Password"
              error={errors.password}
              touched={touched.password}
              type="password"
            />
            <FormControl isInvalid={submissionError !== undefined}>
              <FormErrorMessage>{submissionError}</FormErrorMessage>
            </FormControl>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              isLoading={isSubmitting}
              isDisabled={!isValid}
              loadingText="Logging in"
              type="submit"
            >
              Log In
            </Button>

            <LinkText to={`/forgot-password/`}>Forgot your password?</LinkText>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

const useRedirect = () => {
  const navigate = useNavigate();
  const session = useAtomValue(sessionAtom);

  useEffect(() => {
    if (session) {
      navigate(routes.root);
    }
  }, [navigate, session]);
};

export const LogIn = () => {
  useRedirect();

  return (
    <FormContainer>
      <Heading as="h2" size="lg">
        Log In
      </Heading>
      <Text pt={2}>
        New here? <LinkText to="/register">Go to registration.</LinkText>
      </Text>
      <Spacer h={8} />
      <LogInForm />
    </FormContainer>
  );
};
